import { useState } from "react";
import { useLocation } from "wouter";
import {
  Link2,
  Loader2,
  X,
  Star,
  Tag,
  Sparkles,
  Check,
  CheckCircle2,
  ExternalLink,
  FolderKanban,
  CornerDownLeft,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Thumb } from "@/components/Thumb";
import { useUiStore } from "@/lib/stores/uiStore";
import { useItemsStore } from "@/lib/stores/itemsStore";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const DEFAULT_URL = "https://youtube.com/watch?v=...";

const detectPlatform = (url) => {
  const u = (url || "").toLowerCase();
  if (u.includes("youtube") || u.includes("youtu.be")) return "YouTube";
  if (u.includes("tiktok")) return "TikTok";
  if (u.includes("x.com") || u.includes("twitter")) return "X";
  return "Web";
};

const PREVIEWS = {
  YouTube: { kind: "Vídeo de YouTube detectado", title: "Automatiza tu inbox de contenido con IA", meta: "17 min · demo en directo", tags: ["IA", "automatización"] },
  TikTok: { kind: "Reel de TikTok detectado", title: "Reel: 3 trucos de productividad para creadores", meta: "0:45", tags: ["productividad", "creadores"] },
  X: { kind: "Hilo de X detectado", title: "Hilo: stack mínimo para un side-project", meta: "hilo · 8 tuits", tags: ["dev", "side-project"] },
  Web: { kind: "Artículo web detectado", title: "Guía: organiza tu conocimiento sin fricción", meta: "blog · 8 min lectura", tags: ["PKM", "producto"] },
};

const Step = ({ n, title, hint, state }) => (
  <div
    className={cn(
      "flex-1 rounded-lg border p-3 transition-colors",
      state === "active" && "border-primary/50 bg-accent/50",
      state === "done" && "border-green-600/30",
    )}
  >
    <span
      className={cn(
        "inline-flex w-6 h-6 rounded-full border items-center justify-center text-xs font-semibold",
        state === "active" && "border-primary text-primary",
        state === "done" && "border-green-600 bg-green-600 text-white",
        state === "idle" && "text-muted-foreground",
      )}
    >
      {state === "done" ? <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> : n}
    </span>
    <p className="mt-2 text-sm font-medium">{title}</p>
    <p className="text-xs text-muted-foreground">{hint}</p>
  </div>
);

export const CaptureModal = () => {
  const open = useUiStore((s) => s.captureOpen);
  const closeCapture = useUiStore((s) => s.closeCapture);
  const { createItem, setStatus, projects } = useItemsStore();
  const [, navigate] = useLocation();
  const [phase, setPhase] = useState("idle"); // idle | analyzing | saved
  const [url, setUrl] = useState(DEFAULT_URL);
  const [important, setImportant] = useState(false);
  const [project, setProject] = useState(projects[0]?.id);

  const platform = detectPlatform(url);
  const preview = url && url !== DEFAULT_URL && url.length > 12 ? PREVIEWS[platform] : null;

  const handleClose = () => {
    closeCapture();
    setTimeout(() => {
      setPhase("idle");
      setUrl(DEFAULT_URL);
      setImportant(false);
    }, 250);
  };

  const handleSave = () => {
    if (phase !== "idle") return;
    setPhase("analyzing");
    setTimeout(() => {
      const id = createItem(url);
      if (important) setStatus(id, "importante");
      setPhase("saved");
    }, 1300);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) setUrl(text.trim());
    } catch {
      setUrl("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-2xl rounded-[14px] p-6 md:p-8" data-testid="capture-modal">
        <DialogHeader className="text-left space-y-1">
          <DialogTitle className="font-serif text-2xl md:text-3xl">Compartir con Aviando</DialogTitle>
          <DialogDescription className="text-muted-foreground text-[15px]">
            Pega un enlace y me encargo del resto.
          </DialogDescription>
        </DialogHeader>

        {phase !== "saved" && (
          <div className="space-y-4">
            {/* Input URL */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                <Input
                  value={url}
                  autoFocus
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSave()}
                  className="pl-9 pr-8 font-mono text-sm"
                  aria-label="URL del contenido"
                  data-testid="capture-url-input"
                  disabled={phase === "analyzing"}
                />
                {url && (
                  <button
                    type="button"
                    onClick={() => setUrl("")}
                    aria-label="Borrar URL"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" strokeWidth={1.75} />
                  </button>
                )}
              </div>
              <Button onClick={handlePaste} variant="secondary" className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="capture-paste-button">
                Pegar
              </Button>
            </div>

            {/* Preview de lo detectado */}
            {preview && phase === "idle" && (
              <div className="rounded-lg border bg-card p-4 flex items-start gap-3.5 animate-fade-up" data-testid="capture-preview">
                <Thumb platform={platform} size="row" className="w-14 h-14" />
                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-green-700 dark:text-green-400">
                    <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2} /> {preview.kind}
                  </p>
                  <p className="mt-1 font-serif text-lg leading-snug truncate">{preview.title}</p>
                  <p className="text-[13px] text-muted-foreground">{preview.meta}</p>
                  <div className="mt-1.5 flex gap-1.5">
                    {preview.tags.map((t) => (
                      <span key={t} className="rounded-md bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
                <Button asChild variant="outline" size="sm" className="shrink-0">
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} /> Ver en {platform}
                  </a>
                </Button>
              </div>
            )}

            {/* Opciones */}
            {phase === "idle" && (
              <>
                <div>
                  <p className="text-sm font-medium mb-1.5">Guardar en</p>
                  <Select value={project} onValueChange={setProject}>
                    <SelectTrigger className="w-full sm:w-80" data-testid="capture-project-select">
                      <span className="flex items-center gap-2">
                        <FolderKanban className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                        <SelectValue placeholder="Sin proyecto" />
                      </span>
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid sm:grid-cols-2 gap-2.5">
                  <button
                    type="button"
                    onClick={() => setImportant((v) => !v)}
                    data-testid="capture-important-toggle"
                    className={cn(
                      "rounded-lg border p-3 text-left transition-colors",
                      important ? "border-primary/50 bg-accent/50" : "hover:border-teal-300/60",
                    )}
                  >
                    <span className="flex items-center gap-2 text-sm font-medium">
                      <Star className={cn("h-4 w-4", important ? "text-primary" : "text-muted-foreground")} strokeWidth={1.75} fill={important ? "currentColor" : "none"} />
                      Marcar como importante
                    </span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">Lo destacaré en tu inbox</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {}}
                    className="rounded-lg border p-3 text-left hover:border-teal-300/60 transition-colors"
                  >
                    <span className="flex items-center gap-2 text-sm font-medium">
                      <Tag className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} /> Añadir etiquetas
                    </span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">El agente las propondrá también (opcional)</span>
                  </button>
                </div>

                <div className="rounded-lg bg-accent/60 border border-primary/20 px-3.5 py-2.5 text-sm flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="flex items-center gap-1.5 font-medium">
                    <Sparkles className="h-4 w-4 text-primary" strokeWidth={1.75} /> El agente de IA lo analizará y te lo dejará listo:
                  </span>
                  {["Resumen", "Temática", "Etiquetas", "Utilidad"].map((t) => (
                    <span key={t} className="flex items-center gap-1 text-[13px] text-muted-foreground">
                      <Check className="h-3.5 w-3.5 text-green-700" strokeWidth={2} /> {t}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Pasos 1-2-3 */}
        <div className="flex gap-3" data-testid="capture-steps">
          <Step n={1} title="Listo para capturar" hint="Pega un enlace y pulsa Enter" state={phase === "idle" ? "active" : "done"} />
          <Step
            n={2}
            title={phase === "analyzing" ? "Analizando con IA…" : "Analizando con IA"}
            hint={phase === "analyzing" ? "detectando plataforma, resumen y etiquetas" : "Esto puede tardar unos segundos"}
            state={phase === "analyzing" ? "active" : phase === "saved" ? "done" : "idle"}
          />
          <Step
            n={3}
            title="Guardado en tu inbox"
            hint="El agente lo está enriqueciendo"
            state={phase === "saved" ? "done" : "idle"}
          />
        </div>

        {phase === "analyzing" && (
          <div className="flex items-center justify-center gap-2.5 py-1 text-[15px] font-medium animate-fade-up" data-testid="capture-analyzing">
            <Loader2 className="h-5 w-5 animate-spin text-warning" strokeWidth={1.75} />
            Analizando con IA… <span className="text-sm text-muted-foreground font-normal">detectando plataforma, resumen y etiquetas.</span>
          </div>
        )}

        {phase === "saved" && (
          <div className="py-2 flex flex-col items-center text-center gap-2 animate-fade-up" data-testid="capture-saved">
            <CheckCircle2 className="h-9 w-9 text-success" strokeWidth={1.5} />
            <p className="text-[15px] font-medium">Guardado en tu inbox.</p>
            <p className="text-sm text-muted-foreground -mt-1">El agente lo está enriqueciendo.</p>
            <div className="flex gap-2 mt-1.5">
              <Button variant="ghost" onClick={handleClose} data-testid="capture-close-button">
                Cerrar
              </Button>
              <Button
                onClick={() => {
                  handleClose();
                  navigate("/inbox");
                }}
                data-testid="capture-goto-inbox-button"
              >
                Ver en el inbox
              </Button>
            </div>
          </div>
        )}

        {phase === "idle" && (
          <div className="flex items-center justify-between gap-3 pt-1">
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <CornerDownLeft className="h-3.5 w-3.5" strokeWidth={1.75} /> Consejo: pega y pulsa Enter para capturar al instante
            </p>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={handleClose} data-testid="capture-cancel-button">
                Cancelar
              </Button>
              <Button onClick={handleSave} data-testid="capture-save-button">
                Guardar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
